import { useEffect, useState, type ReactNode } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type FileCategorisationRow = {
  id: string;
  name: string;
  /** Pre-rendered thumbnail node (e.g. <FileThumbnail>). Bearer-token thumbnail
   *  endpoints can't feed a bare <img src>, so callers hand in the node itself;
   *  when omitted the dialog shows a muted placeholder square. */
  thumbnail?: ReactNode;
  categoryId?: string;
};

export type FileCategory = {
  id: string;
  label: string;
};

export interface FileCategoriseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: FileCategorisationRow[];
  categories: FileCategory[];
  onSave: (rows: Array<{ id: string; categoryId: string | null }>) => Promise<void>;
  onSkip?: () => void;
  saving?: boolean;
}

/**
 * Modal for batch file categorisation. Auto-opens after multi-file upload
 * (see JobDetailPage Files tab) or manually via a row-level Categorise action.
 *
 * Contract:
 * - Draft state resets every time `open` transitions to `true` (sync'd from `files`).
 * - `onSave` is awaited; the dialog closes only on successful resolution.
 *   Callers are responsible for surfacing toast/error on rejection — the
 *   dialog stays open silently if `onSave` throws.
 * - `onSkip` closes the dialog without saving; files keep their default
 *   `Uncategorised` category.
 */
export function FileCategoriseDialog({
  open, onOpenChange, files, categories, onSave, onSkip, saving = false,
}: FileCategoriseDialogProps) {
  const [draft, setDraft] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (open) {
      setDraft(Object.fromEntries(files.map((f) => [f.id, f.categoryId])));
    }
  }, [open, files]);

  const handleSave = async () => {
    try {
      await onSave(files.map((f) => ({ id: f.id, categoryId: draft[f.id] ?? null })));
      onOpenChange(false);
    } catch {
      // Caller is expected to surface user-facing errors via toast. We
      // swallow the rejection here so the unhandled-promise warning
      // doesn't fire (the dialog button's onClick uses `void handleSave()`
      // and can't observe the rejection). The dialog stays open so the
      // user can retry — matches the documented contract.
    }
  };

  const handleSkip = () => {
    onSkip?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Categorise files</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="w-12 py-2"><span className="sr-only">Thumbnail</span></th>
                <th className="py-2">File</th>
                <th className="w-44 py-2">Category</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} className="border-t border-border">
                  <td className="py-2">
                    {f.thumbnail ?? <div className="h-8 w-8 rounded bg-muted" />}
                  </td>
                  <td className="py-2">{f.name}</td>
                  <td className="py-2">
                    <Select
                      value={draft[f.id] ?? ""}
                      onValueChange={(v) => setDraft((d) => ({ ...d, [f.id]: v }))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent
                        position="popper"
                        side="bottom"
                        align="start"
                        className="z-[60]"
                      >
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleSkip} disabled={saving}>Skip All</Button>
          <Button onClick={() => { void handleSave(); }} disabled={saving}>
            {saving ? "Saving…" : "Save Categories"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
